import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Tag, 
  Space, 
  Divider, 
  Alert,
  Spin,
  Table,
  Button
} from 'antd';
import { 
  FiUser, 
  FiMapPin, 
  FiCalendar, 
  FiCheck, 
  FiX, 
  FiClock,
  FiFileText,
  FiInfo,
  FiExternalLink
} from 'react-icons/fi';
import { TbTestPipe2Filled } from "react-icons/tb";
import { useTranslation } from 'react-i18next';
import dayjs from '../../../utils/dayjs';
import { getSubjectDetails } from '../../../actions/subjectActions';

const { Text, Title } = Typography;

const SubjectDetails = ({ subjectId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const subjectDetails = useSelector(state => state.subjectDetails);
  const { loading, subjects, error } = subjectDetails;
  
  // Get the specific subject data for this subjectId
  const subject = subjects[subjectId];

  // Debug log to verify correct subjectId is being used
  console.log('SubjectDetails render:', { subjectId, hasSubject: !!subject, allSubjectIds: Object.keys(subjects) });

  useEffect(() => {
    if (subjectId && !subjects[subjectId]) {
      console.log('Loading subject details for:', subjectId);
      dispatch(getSubjectDetails(subjectId));
    }
  }, [dispatch, subjectId, subjects]);

  if (loading && !subject) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && !subject) {
    return (
      <Alert
        message={t('Error loading subject details')}
        description={Array.isArray(error) ? error.join(', ') : error}
        type="error"
        showIcon
      />
    );
  }

  if (!subject) {
    return (
      <Alert
        message={t('No data available')}
        type="info"
        showIcon
      />
    );
  }

  const getStatusTag = (status, type = 'default') => {
    const statusColors = {
      'APPROVED': 'green',
      'REJECTED': 'red',
      'PENDING': 'orange',
      'ACTIVE': 'blue',
      'ENROLLED': 'green',
      'COMPLETED': 'blue'
    };
    
    return (
      <Tag color={statusColors[status] || type}>
        {status}
      </Tag>
    );
  };

  const getBooleanTag = (value, trueText = 'Yes', falseText = 'No') => (
    <Tag color={value ? 'green' : 'red'}>
      {value ? trueText : falseText}
    </Tag>
  );

  const labResultColumns = [
    {
      title: t('Test Name'),
      dataIndex: 'testName',
      key: 'testName',
      render: (text, record) => (
        <Space>
          <TbTestPipe2Filled />
          <span>{text}</span>
          <Text type="secondary">({record.testCode})</Text>
        </Space>
      )
    },
    {
      title: t('Result'),
      key: 'result',
      render: (_, record) => (
        <Space>
          {record.numericResult && <Text strong>{record.numericResult}</Text>}
          {record.resultValue && <Text>{record.resultValue}</Text>}
          {record.units && <Text type="secondary">{record.units}</Text>}
        </Space>
      )
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status)
    },
    {
      title: t('Result Date'),
      dataIndex: 'resultDate',
      key: 'resultDate',
      render: (date) => (
        <Space>
          <FiCalendar />
          {date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'}
        </Space>
      )
    },
    {
      title: t('Receipt Date'),
      dataIndex: 'dateOfReceipt',
      key: 'dateOfReceipt',
      render: (date) => (
        <Space>
          <FiClock />
          {date ? dayjs(date).format('DD/MM/YYYY') : '-'}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Lab Results - Full width section */}
      {subject.labResults && subject.labResults.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <Card 
            size="small" 
            title={
              <Space size={8}>
                <TbTestPipe2Filled style={{ color: '#fa8c16' }} />
                <Text strong style={{ fontSize: '13px' }}>
                  {t('Lab Results')} 
                  <Text style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '8px' }}>
                    ({subject.labResults.length} {t('results')})
                  </Text>
                </Text>
              </Space>
            }
            bodyStyle={{ padding: '12px' }}
          >
            <Table
              dataSource={subject.labResults}
              columns={labResultColumns}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: 600 }}
              style={{ 
                margin: 0,
                borderRadius: '6px'
              }}
            />
          </Card>
        </div>
      )}
    </div>
  );
};

export default SubjectDetails;
